import { useMemo } from 'react';
import type { FlatworkEvent, JobWithContext, Stage } from '../lib/database.types';
import { FLATWORK_TYPE_LABELS } from '../lib/database.types';
import { isFlatworkDone, isWaitingOnFlatwork, overallStatus } from '../lib/boardStatus';
import { cn, formatDateCompact } from '../lib/utils';
import { StatusBadge } from '../components/ui';
import { NotesCell } from './NotesCell';
import { FwReadyCell } from './FwReadyCell';

interface JobRowProps {
    job: JobWithContext;
    stages: Stage[];
    canEdit: boolean;
    flatwork: FlatworkEvent[];
    /** Selection column is only rendered for admins, who are the only ones who can act on a selection. */
    showSelect: boolean;
    /** Column ids on screen, in order. Same list the header renders from. */
    visibleColIds: string[];
    /**
     * Squeeze the row to one line.
     *
     * Andrea, 2 Sep: "the rows are too big to be able to do that in one
     * snippet... I do like the current size of it for daily updating, I would
     * only have to utilize that to snippet and send."
     *
     * So this is a toggle she flips to send and flips back, not a saved
     * setting. Height comes from the three stacked elements in every task
     * cell, the date, the crew and the status badge, plus py-2.5 top and
     * bottom. Compact keeps the date, keeps the cell tint that carries the
     * status at a glance, and drops the rest.
     */
    compact: boolean;
    selected: boolean;
    onToggleSelect: () => void;
    onScheduleStage: (
        jobId: string,
        jobStageId: string,
        stageName: string,
        jobLabel: string,
        currentDate: string | null,
        currentCrewId: string | null
    ) => void;
    onScheduleFlatwork: () => void;
    onEditJob: () => void;
}

/** Pull the "FW Ready: YYYY-MM-DD" date Andrea stores in the notes field. */
function extractFwReady(notes: string | null): string | null {
    if (!notes) return null;
    const m = notes.match(/FW Ready:\s*(\d{4}-\d{2}-\d{2})/i);
    return m ? m[1] : null;
}

/**
 * Width per column id. Stage columns all share one width.
 *
 * Both Job and Notes are capped. Neither is flexible, and that is deliberate.
 *
 * Job used to be 1.5fr, which meant hiding Pin through Pour left it as the only
 * stretchy column and it swallowed every freed pixel, opening a gap between the
 * job name and the notes wide enough to ruin a screen grab. Andrea, 2 Sep.
 *
 * Moving that flex onto Notes fixed the gap and broke something worse. This
 * grid lives inside a min-w-max container, and under max-content sizing a
 * browser resolves fr tracks by taking the largest content-to-flex-factor ratio
 * across every flexible track and applying it to all of them. Notes holds the
 * longest text on the board, so its full unwrapped width became the basis and
 * every stage column at 1fr inherited it. Andrea, 3 Sep: "Every column is now
 * extremely long and I have to scroll to the side to see everything."
 *
 * So neither gets a flex unit. The stage columns keep theirs because their
 * content is a short date and they can absorb slack without distorting
 * anything. Notes is capped wider than it was, 420 rather than 280, which is
 * the readable gain without the risk.
 */
const COLUMN_WIDTH: Record<string, string> = {
    job: 'minmax(260px, 420px)',
    notes: 'minmax(180px, 420px)',
    fwReady: 'minmax(96px, 120px)',
    fwSched: 'minmax(96px, 120px)',
    crew: 'minmax(90px, 110px)',
    status: '100px',
};

const STAGE_WIDTH = 'minmax(110px, 1fr)';

export function columnWidth(colId: string): string {
    return colId.startsWith('stage:') ? STAGE_WIDTH : (COLUMN_WIDTH[colId] ?? STAGE_WIDTH);
}

/**
 * Grid template, built from the list of columns actually on screen.
 *
 * Takes the visible ids rather than a stage count, because columns can now be
 * hidden per person and a count cannot express which ones. The same list drives
 * the header in BoardPage and the cells below, so a hidden column cannot leave
 * the two out of step. That was the trap the service board reorder hit: one
 * list moved and the other did not, and every heading sat over the wrong data.
 */
export function rowGridTemplate(visibleColIds: string[], showSelect: boolean): string {
    return [showSelect ? '40px' : null, ...visibleColIds.map(columnWidth)]
        .filter(Boolean)
        .join(' ');
}

export function JobRow({
    job,
    stages,
    canEdit,
    flatwork,
    showSelect,
    visibleColIds,
    compact,
    selected,
    onToggleSelect,
    onScheduleStage,
    onScheduleFlatwork,
    onEditJob,
}: JobRowProps) {
    // Index the job's stages by stage_id for O(1) lookup
    const stagesByStageId = useMemo(() => {
        const map = new Map<string, JobWithContext['stages'][number]>();
        for (const js of job.stages) map.set(js.stage_id, js);
        return map;
    }, [job.stages]);

    // Completion reads come from lib/boardStatus so the badge, the pill and the
    // Active/Completed split can never disagree again. They previously did, on
    // 345 jobs: the badge asked whether PPC's stages were done while the pill
    // asked whether all eight were, so a lot with Pour finished and a builder
    // inspection still open rendered a green Complete and no explanation for
    // why it was still sitting on the board.
    const status = overallStatus(job);
    const flatworkDone = isFlatworkDone(flatwork);
    const waitingOnFlatwork = isWaitingOnFlatwork(job, flatwork);

    const pad = compact ? 'px-3 py-0.5' : 'px-3 py-2.5';
    const shown = new Set(visibleColIds);

    const fwReady = job.fw_ready_date ?? extractFwReady(job.notes);
    const jobLabel = `${job.community.name} · Lot ${job.lot_number}`;

    return (
        <div
            className={cn(
                'grid items-stretch border-b border-graphite-200 dark:border-graphite-900 hover:bg-graphite-100/40 dark:hover:bg-graphite-900/40 transition-colors',
                // Line height inherits, so one class here governs every cell.
                // This is what actually sets the floor: the community name
                // renders at base size with a 24px line box, and no amount of
                // padding trimming gets under that.
                compact && 'leading-tight',
                job.needs_review && 'bg-amber-500/[0.03] hover:bg-amber-500/[0.06]',
                // A job held on the board by hand reads differently from one the
                // pipeline put there, so nobody wonders why it will not archive.
                job.force_active && 'bg-signal-500/[0.04] hover:bg-signal-500/[0.07]',
                selected && 'bg-signal-500/[0.10] hover:bg-signal-500/[0.14]'
            )}
            style={{ gridTemplateColumns: rowGridTemplate(visibleColIds, showSelect) }}
        >
            {/* Selection cell */}
            {showSelect ? (
                <label
                    className={cn(
                        pad,
                        'border-r border-graphite-200 dark:border-graphite-900 flex items-start justify-center cursor-pointer select-none hover:bg-graphite-200/60 dark:hover:bg-graphite-800/60'
                    )}
                    title={`Select ${jobLabel}`}
                >
                    <input
                        type="checkbox"
                        checked={selected}
                        onChange={onToggleSelect}
                        className="accent-signal-500 mt-1"
                        aria-label={`Select ${jobLabel}`}
                    />
                </label>
            ) : null}

            {/* Job identity cell. Never hideable, so no guard here. */}
            <button
                type="button"
                onClick={onEditJob}
                disabled={!canEdit}
                className={cn(
                    compact ? 'px-4 py-0.5' : 'px-4 py-2.5',
                    'text-left border-r border-graphite-200 dark:border-graphite-900 flex min-w-0',
                    // The Job cell is the only one still stacking two lines once
                    // the task cells collapse, so it sets the floor for the whole
                    // row. Laying it out sideways puts builder, community and lot
                    // on one line and takes the row from roughly 55px to 30px.
                    compact ? 'flex-row items-baseline gap-2' : 'flex-col gap-0.5',
                    canEdit && 'hover:bg-graphite-100 dark:hover:bg-graphite-900 cursor-pointer',
                    !canEdit && 'cursor-default'
                )}
            >
                <div className="flex items-center gap-2 min-w-0">
                    <span className="font-mono text-xs text-graphite-500 tabular shrink-0">
                        {job.builder.prefix}
                    </span>
                    <span className={cn(
                        'font-display font-medium text-graphite-900 dark:text-graphite-100 truncate',
                        compact && 'text-xs'
                    )}>
                        {job.community.name}
                    </span>
                    {job.needs_review ? (
                        <span
                            className="shrink-0 inline-block w-1.5 h-1.5 rounded-full bg-amber-500"
                            title={job.review_reason ?? 'Needs review'}
                        />
                    ) : null}
                </div>
                <div className="flex items-baseline gap-2 min-w-0">
                    <span className={cn(
                        'font-mono tabular font-semibold text-graphite-900 dark:text-graphite-100 shrink-0',
                        compact ? 'text-xs' : 'text-sm'
                    )}>
                        Lot {job.lot_number}
                    </span>
                    {job.address && !compact ? (
                        <span className="truncate text-xs text-graphite-500">· {job.address}</span>
                    ) : null}
                </div>
                {job.contact_name && !compact ? (
                    <div className="text-[11px] text-graphite-400 dark:text-graphite-600 truncate">
                        {job.contact_name}
                    </div>
                ) : null}
            </button>

            {/* Notes cell — inline editable */}
            {shown.has('notes') ? (
                <NotesCell jobId={job.id} value={job.notes ?? null} canEdit={canEdit} compact={compact} />
            ) : null}

            {/* Stage cells */}
            {stages.filter((stage) => shown.has(`stage:${stage.id}`)).map((stage) => {
                const js = stagesByStageId.get(stage.id);
                if (!js) {
                    return (
                        <div
                            key={stage.id}
                            className={cn(pad, 'border-r border-graphite-200 dark:border-graphite-900 font-mono text-xs text-graphite-300 dark:text-graphite-700')}
                        >
                            —
                        </div>
                    );
                }
                const clickable = canEdit;
                return (
                    <button
                        key={stage.id}
                        type="button"
                        disabled={!clickable}
                        onClick={() =>
                            clickable
                                ? onScheduleStage(job.id, js.id, stage.name, jobLabel, js.scheduled_date, js.crew_id)
                                : undefined
                        }
                        className={cn(
                            pad,
                            'border-r border-graphite-200 dark:border-graphite-900 text-left flex flex-col min-w-0 transition-colors',
                            compact ? 'gap-0 justify-center' : 'gap-1',
                            clickable && 'hover:bg-graphite-200 dark:hover:bg-graphite-800 cursor-pointer',
                            !clickable && 'cursor-default',
                            // Cell tints, not just the badge. Andrea's sheet
                            // fills the whole cell and she reads the board by
                            // colour across a screen of rows, so the tint is
                            // what carries at a glance. Kept well under the
                            // badge's saturation so a row still scans as a row
                            // rather than a block of paint.
                            // Compact hides the status badge, so the tint is the
                            // only thing left carrying the status. At 10 to 14
                            // percent that is too faint to read across a screen
                            // grab, which is what Andrea hit on 2 Sep: "can the
                            // colors stay more vibrant so they are more visible".
                            // Roughly tripled in compact, keeping the relative
                            // weighting, so it reads closer to the saturated
                            // fills on her own sheet. Full rows keep the subtle
                            // tint, where the badge does the work.
                            js.status === 'pending' && (compact ? 'bg-yellow-400/[0.38]' : 'bg-yellow-400/[0.10]'),
                            js.status === 'scheduled' && (compact ? 'bg-signal-400/[0.38]' : 'bg-signal-400/[0.10]'),
                            js.status === 'in_progress' && (compact ? 'bg-orange-400/[0.45]' : 'bg-orange-400/[0.14]'),
                            js.status === 'complete' && (compact ? 'bg-done-500/[0.45]' : 'bg-done-500/[0.14]'),
                            js.status === 'on_hold' && (compact ? 'pattern-hold bg-hold-500/[0.45]' : 'pattern-hold bg-hold-500/[0.14]')
                        )}
                    >
                        {js.scheduled_date || js.actual_completion_date ? (
                            <div className="font-mono text-xs text-graphite-900 dark:text-graphite-100 tabular">
                                {js.status === 'complete' && js.actual_completion_date
                                    ? formatDateCompact(js.actual_completion_date)
                                    : formatDateCompact(js.scheduled_date)}
                            </div>
                        ) : (
                            <div className="font-mono text-xs text-graphite-300 dark:text-graphite-700">—</div>
                        )}
                        {compact ? null : js.crew ? (
                            <div className="font-mono text-[10px] uppercase tracking-wider text-signal-700 dark:text-signal-400 truncate">
                                {js.crew.name}
                            </div>
                        ) : (
                            <div className="h-3" />
                        )}
                        {compact ? null : <StatusBadge status={js.status} size="sm" />}
                    </button>
                );
            })}

            {/* FW Ready cell — date plus the builder's go-ahead */}
            {shown.has('fwReady') ? (
            <FwReadyCell
                jobId={job.id}
                value={fwReady}
                readyConfirmed={job.fw_ready_confirmed}
                canEdit={canEdit}
                compact={compact}
            />
            ) : null}

            {/* FW Scheduled cell — one row per flatwork type, shared date.
                Turns green once flatwork is marked complete. */}
            {shown.has('fwSched') ? (
            <button
                type="button"
                disabled={!canEdit}
                onClick={() => (canEdit ? onScheduleFlatwork() : undefined)}
                className={cn(
                    pad,
                    'border-r border-graphite-200 dark:border-graphite-900 text-left flex flex-col gap-1 justify-center min-w-0 transition-colors',
                    canEdit && 'hover:bg-graphite-200 dark:hover:bg-graphite-800 cursor-pointer',
                    !canEdit && 'cursor-default',
                    flatworkDone && (compact ? 'bg-done-500/[0.45]' : 'bg-done-500/[0.08]')
                )}
            >
                {flatworkDone ? (
                    <>
                        <div className="font-mono text-xs text-done-700 dark:text-done-500 tabular">
                            {flatwork[0].actual_completion_date
                                ? formatDateCompact(flatwork[0].actual_completion_date)
                                : formatDateCompact(flatwork[0].scheduled_date)}
                        </div>
                        {compact ? null : (
                            <div className="font-mono text-[9px] uppercase tracking-wider text-done-600 dark:text-done-500">
                                Complete
                            </div>
                        )}
                    </>
                ) : flatwork.length > 0 && flatwork[0].scheduled_date ? (
                    <>
                        <div className="font-mono text-xs text-graphite-900 dark:text-graphite-100 tabular">
                            {formatDateCompact(flatwork[0].scheduled_date)}
                        </div>
                        {compact ? null : (
                            <div className="font-mono text-[10px] uppercase tracking-wider text-signal-700 dark:text-signal-400 truncate">
                                {flatwork.map((f) => FLATWORK_TYPE_LABELS[f.event_type]).join(', ')}
                            </div>
                        )}
                    </>
                ) : (
                    <div className="font-mono text-xs text-graphite-300 dark:text-graphite-700">—</div>
                )}
            </button>
            ) : null}

            {/* Crew cell — the crew for the whole job, which is what the office
                filters and exports by at close of day. Distinct from the crew
                shown inside each stage cell. Opens the job dialog to change. */}
            {shown.has('crew') ? (
            <button
                type="button"
                disabled={!canEdit}
                onClick={() => (canEdit ? onEditJob() : undefined)}
                title={canEdit ? 'Open the job to change its crew' : undefined}
                className={cn(
                    pad,
                    'border-r border-graphite-200 dark:border-graphite-900 text-left flex items-center min-w-0 transition-colors',
                    canEdit && 'hover:bg-graphite-200 dark:hover:bg-graphite-800 cursor-pointer',
                    !canEdit && 'cursor-default'
                )}
            >
                {job.crew ? (
                    <span className="font-mono text-[11px] uppercase tracking-wider text-signal-700 dark:text-signal-400 truncate">
                        {job.crew.name}
                    </span>
                ) : (
                    <span className="font-mono text-xs text-graphite-300 dark:text-graphite-700">—</span>
                )}
            </button>
            ) : null}

            {/* Overall status column */}
            {shown.has('status') ? (
            <div className={cn(pad, 'flex items-center justify-center', compact && 'whitespace-nowrap')}>
                {/*
                  * These two pills are the reason a compact row is not as short
                  * as it looks like it should be. "Slab Done · FW Pending" wraps
                  * to three lines inside a 100px column, so any row carrying it
                  * runs about 45px while a row with a plain status badge runs
                  * about 33px. Compact shortens the wording and stops the wrap.
                  * The title attribute keeps the full meaning on hover.
                  */}
                {waitingOnFlatwork ? (
                    <span
                        className={cn(
                            'inline-flex items-center gap-1 border rounded font-mono uppercase tracking-wider text-[9px] px-1.5 py-0.5 bg-signal-500/10 text-signal-700 border-signal-500/40 dark:text-signal-300 text-center leading-tight',
                            compact && 'whitespace-nowrap'
                        )}
                        title="Slab poured. Waiting on flatwork before this job leaves the board."
                    >
                        {compact ? 'FW Pend' : 'Slab Done · FW Pending'}
                    </span>
                ) : job.force_active ? (
                    <span
                        className={cn(
                            'inline-flex items-center gap-1 border rounded font-mono uppercase tracking-wider text-[9px] px-1.5 py-0.5 bg-signal-500/10 text-signal-700 border-signal-500/40 dark:text-signal-300 text-center leading-tight',
                            compact && 'whitespace-nowrap'
                        )}
                        title="Held on the board by hand. Open the job and send it to Completed when it is really done."
                    >
                        {compact ? 'Kept' : 'Kept On Board'}
                    </span>
                ) : (
                    <StatusBadge status={status} size="sm" />
                )}
            </div>
            ) : null}
        </div>
    );
}
